<template>
  <nav class="list-steps">
    <ol class="list-steps__steps">
      <li
        v-for="(step, index) in steps"
        :key="step.stepId"
        class="list-steps__step"
        :class="{
          'is-active': step.isActive,
          'is-invalid-step': step.isFailed && !step.groups
        }"
      >
        <a
          v-if="step.isClickable"
          href="javascript:;"
          class="list-steps__step-link"
          :class="{
            'is-clickable': step.isClickable,
          }"
          @click.prevent="$emit('go-to-step', {
            step,
            index: index + 1,
            formObserver,
          });"
        >
          <strong class="list-steps__step-label">{{ step.stepLabel }}</strong>
        </a>
        <strong
          v-else
          class="list-steps__step-label"
        >{{ step.stepLabel }}</strong>
        <ul
          v-if="step.isActive && step.groups"
          class="list-steps__groups"
        >
          <li
            v-for="group in getRenderedStepGroups(step, formObserver)"
            :key="group.groupId"
            :style="{ color: getGroupColor(group) }"
            class="list-steps__group"
            :class="{
              'is-valid': group.isValid,
              'is-invalid': formObserver.validated && formObserver.failed && !group.isValid,
            }"
          >
            <a
              href="javascript:;"
              class="list-steps__group-link"
              @click.prevent="$emit('go-to-group', {
                group,
                step,
                formObserver,
              });"
            >
              <v-icon
                v-if="group.isValid || formObserver.validated && formObserver.failed"
                class="icon"
                x-small
              >
                <template v-if="group.isValid">mdi-check</template>
                <template v-else>mdi-close</template>
              </v-icon>
              <strong class="list-steps__group-label">{{ group.groupLabel }}</strong>
            </a>
          </li>
        </ul>
      </li>
    </ol>
  </nav>
</template>

<script>
import {
  flatten,
  kebabCase,
} from 'lodash';

export default {
  name: 'list-steps',
  props: {
    steps: {
      type: Array,
      required: true,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    currentDriver: {
      type: Number,
      required: true,
    },
  },
  methods: {
    getRenderedStepGroups (step, formObserver) {
      return step.groups.map(group => {
        const groupFields = flatten(group.sections.map(section => typeof (section) === 'string' ? section : section.fields));

        const renderedGroupFields = groupFields
          .map(field => `${step.stepId}_${kebabCase(field)}`)
          .filter(fieldId => {
            return Object.keys(formObserver.fields)
              .map(key => formObserver.fields[key].id)
              .includes(fieldId);
          });

        const isRendered = renderedGroupFields.length > 0;

        const isValid = renderedGroupFields.every(fieldId => formObserver.fields[fieldId].valid);
        const isInvalid = renderedGroupFields.some(fieldId => formObserver.fields[fieldId].invalid);
        const isDirty = renderedGroupFields.some(fieldId => formObserver.fields[fieldId].dirty);

        return {
          ...group,
          isRendered,
          isValid,
          isInvalid,
          isDirty,
        };
      })
        .filter(({ isRendered }) => isRendered);
    },
    getGroupColor ({ isValid, isInvalid, isDirty }) {
      if (isValid) return this.$vuetify.theme.themes.light.success;
      return this.formObserver.validated && this.formObserver.failed ? this.$vuetify.theme.themes.light.error : 'currentColor';
    },
  },
};
</script>

<style lang="scss" scoped>
.list-steps {
  max-width: 190px;
  border: 1px solid $clr-heather;
  background: $clr-white;
  border-radius: 6px;
  padding: 24px 14px;

  &__steps {
    list-style: none;
    padding: 0;
    counter-reset: counter;
  }

  &__step {
    counter-increment: counter;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      border-left: 1px solid #d0d1d8;
      top: 37px;
      bottom: 13px;
      left: 12px;
    }
  }

  &__step-link {
    color: currentColor;
    display: block;
    font-size: 16px;

    &:focus {
      outline: none;
    }
  }

  &__step-link:hover,
  &__step.is-active &__step-link {
    color: var(--v-breadcrumb-text-base, var(--v-primary-base, $clr-primary));
  }

  &__step-label {
    display: flex;
    align-items: center;
    font-size: 16px;

    &::before {
      content: counter(counter);
      flex: 0 0 24px;
      width: 24px;
      height: 24px;
      margin-right: 10px;
      border-radius: 50%;
      background-color: #818d97;
      font-weight: bold;
      font-size: 12px;
      line-height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--v-breadcrumb-number-base, $clr-white);
      transition: background-color 0.15s ease-in-out;
    }
  }

  &__step-link:hover &__step-label::before {
    background-color: var(--v-breadcrumb-bg-base, var(--v-primary-base, $clr-primary));
  }

  &__step.is-active &__step-label::before {
    background-color: var(--v-breadcrumb-bg-base, var(--v-primary-base, $clr-primary));
    border-radius: 50% 50% 0 50%;
  }

  &__step.is-invalid-step &__step-label {
    color: var(--v-error-base, $clr-error);

    &::before {
      background-color: var(--v-error-base, $clr-error);
    }
  }

  &__step:not(:last-child) {
    min-height: 24px + 45px;
  }

  $group-lh: 15px;

  &__groups {
    padding-left: 22px;
    margin: 13px 0 13px 12px;
    min-height: 19px;
    border-left: 1px solid #d0d1d8;
    font-size: 14px;
    line-height: $group-lh;
    font-weight: 500;
    color: var(--v-breadcrumb-text-base, var(--v-primary-base, $clr-primary));
  }

  &__group-link {
    text-decoration: underline;
    color: currentColor;
  }

  &__group-label {
    text-transform: capitalize;
  }

  &__group {
    position: relative;

    & + li { margin-top: 1.2em; }

    .icon {
      position: absolute;
      right: 100%;
      top: $group-lh / 2;
      transform: translateY(-50%);
      margin-right: 3px;
    }

    &.is-valid {
      color: var(--v-success-base, $clr-success);
    }

    &.is-invalid {
      color: var(--v-error-base, $clr-error);
    }
  }
}
</style>
